import { useMutation } from '@tanstack/react-query';
import { Building2, KeyRound, Pencil, Plus, Search, ShieldCheck, Trash2, Users } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/features/auth';
import { addClubMember, useClubsWithFacilitiesQuery } from '@/features/club';
import {
  fullNameOf,
  useAdminUsersQuery,
  useCreateUserMutation,
  useDeleteUserMutation,
  useSetUserPasswordMutation,
  useSetUserRolesMutation,
  useUpdateUserMutation,
  type EditableUserStatus,
  type Gender,
  type UserProfile,
} from '@/features/user';
import { ApiError } from '@/shared/api/ApiError';
import {
  Alert,
  Avatar,
  Badge,
  Button,
  Checkbox,
  EmptyState,
  FormField,
  IconButton,
  Input,
  Modal,
  PageHeader,
  PasswordInput,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableHeaderCell,
  TableRow,
  useToast,
} from '@/shared/ui';

/**
 * Role globalne, ktorymi zarzadza panel. ADMIN poza lista (API go nie nadaje),
 * OWNER tez — jest nadawany automatycznie przez przypisanie do klubu i to
 * czlonkostwo jest jedynym mechanizmem zostawania wlascicielem.
 */
const EDITABLE_ROLES = ['PLAYER', 'TRAINER'] as const;

/** Systemowe role klubowe do przypisania czlonkostwa. */
const CLUB_ROLES = ['OWNER', 'MANAGER', 'TRAINER', 'WORKER'] as const;

/** Ton plakietki statusu konta. */
const STATUS_TONE: Record<string, 'success' | 'warning' | 'neutral'> = {
  ACTIVE: 'success',
  INVITED: 'warning',
  INACTIVE: 'neutral',
};

/**
 * Panel zarzadzania uzytkownikami systemu — wylacznie dla roli ADMIN.
 *
 * Ukrycie zakladki w nawigacji to tylko wygoda — twarda kontrola dostepu
 * siedzi w backendzie (`GET /api/users` i mutacje wymagaja roli ADMIN).
 */
export function UsersAdminPage() {
  const { t } = useTranslation('common');
  const { user: currentUser, hasRole } = useAuth();
  const { showToast } = useToast();
  const isAdmin = hasRole('ADMIN');

  const usersQuery = useAdminUsersQuery(isAdmin);
  const clubsQuery = useClubsWithFacilitiesQuery();

  const createMutation = useCreateUserMutation();
  const updateMutation = useUpdateUserMutation();
  const deleteMutation = useDeleteUserMutation();
  const rolesMutation = useSetUserRolesMutation();
  const passwordMutation = useSetUserPasswordMutation();
  const assignMutation = useMutation({
    mutationFn: ({ clubId, userId, role }: { clubId: string; userId: string; role: string }) =>
      addClubMember(clubId, userId, role),
  });

  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<UserProfile | null>(null);
  const [rolesFor, setRolesFor] = useState<UserProfile | null>(null);
  const [assigningFor, setAssigningFor] = useState<UserProfile | null>(null);
  const [passwordFor, setPasswordFor] = useState<UserProfile | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [deleting, setDeleting] = useState<UserProfile | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);

  // Stany formularzy dialogow — proste pola kontrolowane, bez biblioteki formularzy.
  const [createForm, setCreateForm] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    gender: 'MALE' as Gender,
  });
  const [editForm, setEditForm] = useState({
    firstName: '',
    lastName: '',
    gender: 'MALE' as Gender,
    status: 'ACTIVE' as EditableUserStatus,
  });
  const [pickedRoles, setPickedRoles] = useState<Set<string>>(new Set());
  const [assignForm, setAssignForm] = useState({ clubId: '', role: 'MANAGER' });

  const users = useMemo(() => usersQuery.data ?? [], [usersQuery.data]);
  const visible = useMemo(() => {
    const phrase = search.trim().toLowerCase();
    if (!phrase) return users;
    return users.filter((row) =>
      `${row.firstName} ${row.lastName} ${row.email}`.toLowerCase().includes(phrase),
    );
  }, [users, search]);

  /** Komunikat bledu z API albo tekst zastepczy. */
  function describe(error: unknown, fallback: string): string {
    return error instanceof ApiError ? error.message : fallback;
  }

  function openEdit(row: UserProfile) {
    setDialogError(null);
    setEditForm({
      firstName: row.firstName,
      lastName: row.lastName,
      gender: row.gender === 'FEMALE' ? 'FEMALE' : 'MALE',
      status: row.status === 'INACTIVE' ? 'INACTIVE' : 'ACTIVE',
    });
    setEditing(row);
  }

  function openRoles(row: UserProfile) {
    setDialogError(null);
    // OWNER i ADMIN nie sa edytowalne — checkboxy dotycza tylko rol recznych.
    setPickedRoles(
      new Set((row.globalRoles ?? []).filter((role) => role !== 'ADMIN' && role !== 'OWNER')),
    );
    setRolesFor(row);
  }

  function openAssign(row: UserProfile) {
    setDialogError(null);
    setAssignForm({ clubId: '', role: 'MANAGER' });
    setAssigningFor(row);
  }

  function handleCreate() {
    setDialogError(null);
    createMutation.mutate(
      { ...createForm },
      {
        onSuccess: () => {
          setCreateOpen(false);
          showToast({ tone: 'success', description: t('usersAdmin.toast.created') });
        },
        onError: (error) => setDialogError(describe(error, t('usersAdmin.toast.createFailed'))),
      },
    );
  }

  function handleUpdate() {
    if (!editing) return;
    setDialogError(null);
    updateMutation.mutate(
      { id: editing.id, request: { ...editForm } },
      {
        onSuccess: () => {
          setEditing(null);
          showToast({ tone: 'success', description: t('usersAdmin.toast.updated') });
        },
        onError: (error) => setDialogError(describe(error, t('usersAdmin.toast.updateFailed'))),
      },
    );
  }

  function handleRoles() {
    if (!rolesFor) return;
    setDialogError(null);
    // Posiadane OWNER zachowujemy — edycja rol recznych nie moze odbierac
    // wlascicielstwa nadanego przez przypisanie do klubu.
    const preservedOwner = (rolesFor.globalRoles ?? []).includes('OWNER') ? ['OWNER'] : [];
    rolesMutation.mutate(
      { id: rolesFor.id, roles: [...pickedRoles, ...preservedOwner] },
      {
        onSuccess: () => {
          setRolesFor(null);
          showToast({ tone: 'success', description: t('usersAdmin.toast.rolesSaved') });
        },
        onError: (error) => setDialogError(describe(error, t('usersAdmin.toast.rolesFailed'))),
      },
    );
  }

  function handleAssign() {
    if (!assigningFor || !assignForm.clubId) return;
    setDialogError(null);
    assignMutation.mutate(
      { clubId: assignForm.clubId, userId: assigningFor.id, role: assignForm.role },
      {
        onSuccess: () => {
          setAssigningFor(null);
          showToast({ tone: 'success', description: t('usersAdmin.toast.assigned') });
        },
        onError: (error) => setDialogError(describe(error, t('usersAdmin.toast.assignFailed'))),
      },
    );
  }

  function openPassword(row: UserProfile) {
    setDialogError(null);
    setNewPassword('');
    setPasswordFor(row);
  }

  function handlePassword() {
    if (!passwordFor) return;
    setDialogError(null);
    passwordMutation.mutate(
      { id: passwordFor.id, password: newPassword },
      {
        onSuccess: () => {
          setPasswordFor(null);
          showToast({ tone: 'success', description: t('usersAdmin.toast.passwordSet') });
        },
        onError: (error) =>
          setDialogError(describe(error, t('usersAdmin.toast.passwordFailed'))),
      },
    );
  }

  function handleDelete() {
    if (!deleting) return;
    deleteMutation.mutate(deleting.id, {
      onSuccess: () => {
        setDeleting(null);
        showToast({ tone: 'success', description: t('usersAdmin.toast.deleted') });
      },
      onError: (error) => {
        setDeleting(null);
        showToast({ tone: 'danger', description: describe(error, t('usersAdmin.toast.deleteFailed')) });
      },
    });
  }

  const header = (
    <PageHeader title={t('usersAdmin.title')} description={t('usersAdmin.description')} />
  );

  if (!isAdmin) {
    return (
      <>
        {header}
        <EmptyState
          icon={<ShieldCheck />}
          title={t('usersAdmin.forbiddenTitle')}
          description={t('usersAdmin.forbiddenDescription')}
          className="min-h-[16rem]"
        />
      </>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      {header}

      <div className="flex flex-wrap items-center gap-2">
        <Button
          leadingIcon={<Plus aria-hidden="true" />}
          onClick={() => {
            setDialogError(null);
            setCreateForm({ email: '', password: '', firstName: '', lastName: '', gender: 'MALE' });
            setCreateOpen(true);
          }}
        >
          {t('usersAdmin.add')}
        </Button>

        <Input
          inputSize="sm"
          type="search"
          aria-label={t('usersAdmin.search')}
          placeholder={t('usersAdmin.search')}
          leadingIcon={<Search />}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="w-72"
        />
      </div>

      {usersQuery.isError && <Alert tone="danger">{t('usersAdmin.loadError')}</Alert>}
      {usersQuery.isPending && <Skeleton className="h-64 w-full" />}

      {usersQuery.isSuccess && users.length === 0 && (
        <EmptyState
          icon={<Users />}
          title={t('usersAdmin.emptyTitle')}
          description={t('usersAdmin.emptyDescription')}
          className="min-h-[16rem]"
        />
      )}

      {usersQuery.isSuccess && users.length > 0 && visible.length === 0 && (
        <EmptyState
          icon={<Search />}
          title={t('usersAdmin.noMatchTitle')}
          description={t('usersAdmin.noMatchDescription')}
          className="min-h-[16rem]"
        />
      )}

      {visible.length > 0 && (
        <TableContainer>
          <Table className="[&_td]:px-3 [&_th]:px-3">
            <TableHead>
              <TableRow>
                <TableHeaderCell className="w-12">{t('usersAdmin.columns.index')}</TableHeaderCell>
                <TableHeaderCell>{t('usersAdmin.columns.user')}</TableHeaderCell>
                <TableHeaderCell className="w-28">{t('usersAdmin.columns.gender')}</TableHeaderCell>
                <TableHeaderCell className="w-28">{t('usersAdmin.columns.status')}</TableHeaderCell>
                <TableHeaderCell>{t('usersAdmin.columns.roles')}</TableHeaderCell>
                <TableHeaderCell className="w-40 text-right">
                  {t('usersAdmin.columns.actions')}
                </TableHeaderCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {visible.map((row, index) => {
                const self = row.id === currentUser?.id;
                const name = fullNameOf(row);
                return (
                  <TableRow key={row.id}>
                    <TableCell className="text-fg-subtle text-sm tabular-nums">
                      {index + 1}
                    </TableCell>

                    <TableCell>
                      <div className="flex items-center gap-2.5">
                        <Avatar name={name} size="sm" colorful />
                        <div className="min-w-0">
                          <p className="text-fg-strong truncate text-sm font-semibold">
                            {name}
                            {self && (
                              <span className="text-fg-subtle ml-2 text-xs font-normal">
                                {t('usersAdmin.selfNote')}
                              </span>
                            )}
                          </p>
                          <p className="text-fg-muted truncate text-xs">{row.email}</p>
                        </div>
                      </div>
                    </TableCell>

                    <TableCell>
                      <span className="text-fg-muted text-sm">
                        {row.gender
                          ? t(`usersAdmin.gender.${row.gender}`, { defaultValue: row.gender })
                          : '—'}
                      </span>
                    </TableCell>

                    <TableCell>
                      <Badge tone={STATUS_TONE[row.status] ?? 'neutral'} size="sm">
                        {t(`usersAdmin.status.${row.status}`, { defaultValue: row.status })}
                      </Badge>
                    </TableCell>

                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(row.globalRoles ?? []).map((role) => (
                          <Badge key={role} tone={role === 'ADMIN' ? 'brand' : 'neutral'} size="sm">
                            {t(`roles.${role}`, { defaultValue: role })}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>

                    <TableCell>
                      <div className="flex justify-end gap-1">
                        <IconButton
                          variant="secondary"
                          size="sm"
                          icon={<Pencil />}
                          label={t('usersAdmin.actions.edit')}
                          onClick={() => openEdit(row)}
                        />
                        <IconButton
                          variant="secondary"
                          size="sm"
                          icon={<ShieldCheck />}
                          label={t('usersAdmin.actions.roles')}
                          onClick={() => openRoles(row)}
                        />
                        <IconButton
                          variant="secondary"
                          size="sm"
                          icon={<Building2 />}
                          label={t('usersAdmin.actions.club')}
                          onClick={() => openAssign(row)}
                        />
                        <IconButton
                          variant="secondary"
                          size="sm"
                          icon={<KeyRound />}
                          label={t('usersAdmin.actions.password')}
                          onClick={() => openPassword(row)}
                        />
                        <IconButton
                          variant="danger"
                          size="sm"
                          icon={<Trash2 />}
                          label={t('usersAdmin.actions.delete')}
                          // Wlasne konto i konta ADMIN chroni tez backend (409).
                          disabled={self || (row.globalRoles ?? []).includes('ADMIN')}
                          onClick={() => setDeleting(row)}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          <TableFooter>
            {t('usersAdmin.footer', { shown: visible.length, total: users.length })}
          </TableFooter>
        </TableContainer>
      )}

      {/* Nowe konto */}
      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        size="sm"
        title={t('usersAdmin.createDialog.title')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setCreateOpen(false)}>
              {t('usersAdmin.createDialog.cancel')}
            </Button>
            <Button loading={createMutation.isPending} onClick={handleCreate}>
              {t('usersAdmin.createDialog.submit')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          {dialogError && <Alert tone="danger">{dialogError}</Alert>}
          <FormField label={t('usersAdmin.createDialog.email')} required>
            <Input
              type="email"
              value={createForm.email}
              onChange={(event) => setCreateForm({ ...createForm, email: event.target.value })}
            />
          </FormField>
          <FormField label={t('usersAdmin.createDialog.password')} required>
            <PasswordInput
              value={createForm.password}
              onChange={(event) => setCreateForm({ ...createForm, password: event.target.value })}
            />
          </FormField>
          <FormField label={t('usersAdmin.createDialog.firstName')} required>
            <Input
              value={createForm.firstName}
              onChange={(event) => setCreateForm({ ...createForm, firstName: event.target.value })}
            />
          </FormField>
          <FormField label={t('usersAdmin.createDialog.lastName')} required>
            <Input
              value={createForm.lastName}
              onChange={(event) => setCreateForm({ ...createForm, lastName: event.target.value })}
            />
          </FormField>
          <FormField label={t('usersAdmin.createDialog.gender')}>
            <Select
              value={createForm.gender}
              onChange={(event) =>
                setCreateForm({ ...createForm, gender: event.target.value as Gender })
              }
            >
              <option value="MALE">{t('usersAdmin.createDialog.male')}</option>
              <option value="FEMALE">{t('usersAdmin.createDialog.female')}</option>
            </Select>
          </FormField>
        </div>
      </Modal>

      {/* Edycja danych i statusu */}
      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        size="sm"
        title={t('usersAdmin.editDialog.title')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setEditing(null)}>
              {t('usersAdmin.editDialog.cancel')}
            </Button>
            <Button loading={updateMutation.isPending} onClick={handleUpdate}>
              {t('usersAdmin.editDialog.submit')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          {dialogError && <Alert tone="danger">{dialogError}</Alert>}
          <FormField label={t('usersAdmin.editDialog.firstName')} required>
            <Input
              value={editForm.firstName}
              onChange={(event) => setEditForm({ ...editForm, firstName: event.target.value })}
            />
          </FormField>
          <FormField label={t('usersAdmin.editDialog.lastName')} required>
            <Input
              value={editForm.lastName}
              onChange={(event) => setEditForm({ ...editForm, lastName: event.target.value })}
            />
          </FormField>
          <FormField label={t('usersAdmin.editDialog.gender')}>
            <Select
              value={editForm.gender}
              onChange={(event) =>
                setEditForm({ ...editForm, gender: event.target.value as Gender })
              }
            >
              <option value="MALE">{t('usersAdmin.gender.MALE')}</option>
              <option value="FEMALE">{t('usersAdmin.gender.FEMALE')}</option>
            </Select>
          </FormField>
          <FormField
            label={t('usersAdmin.editDialog.status')}
            hint={t('usersAdmin.editDialog.inactiveHint')}
          >
            <Select
              value={editForm.status}
              onChange={(event) =>
                setEditForm({ ...editForm, status: event.target.value as EditableUserStatus })
              }
            >
              <option value="ACTIVE">{t('usersAdmin.status.ACTIVE')}</option>
              <option value="INACTIVE">{t('usersAdmin.status.INACTIVE')}</option>
            </Select>
          </FormField>
        </div>
      </Modal>

      {/* Role globalne */}
      <Modal
        open={rolesFor !== null}
        onClose={() => setRolesFor(null)}
        size="sm"
        title={t('usersAdmin.rolesDialog.title')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setRolesFor(null)}>
              {t('usersAdmin.rolesDialog.cancel')}
            </Button>
            <Button loading={rolesMutation.isPending} onClick={handleRoles}>
              {t('usersAdmin.rolesDialog.submit')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          {dialogError && <Alert tone="danger">{dialogError}</Alert>}
          {EDITABLE_ROLES.map((role) => (
            <Checkbox
              key={role}
              label={t(`roles.${role}`, { defaultValue: role })}
              checked={pickedRoles.has(role)}
              onChange={(event) => {
                const next = new Set(pickedRoles);
                if (event.target.checked) {
                  next.add(role);
                } else {
                  next.delete(role);
                }
                setPickedRoles(next);
              }}
            />
          ))}
          {(rolesFor?.globalRoles ?? []).includes('OWNER') && (
            <Checkbox
              label={t('roles.OWNER')}
              description={t('usersAdmin.rolesDialog.ownerNote')}
              checked
              disabled
            />
          )}
          {(rolesFor?.globalRoles ?? []).includes('ADMIN') && (
            <Checkbox label={t('roles.ADMIN')} checked disabled />
          )}
          <p className="text-fg-subtle text-xs">{t('usersAdmin.rolesDialog.hint')}</p>
        </div>
      </Modal>

      {/* Przypisanie do klubu */}
      <Modal
        open={assigningFor !== null}
        onClose={() => setAssigningFor(null)}
        size="sm"
        title={t('usersAdmin.clubDialog.title')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setAssigningFor(null)}>
              {t('usersAdmin.clubDialog.cancel')}
            </Button>
            <Button
              loading={assignMutation.isPending}
              disabled={!assignForm.clubId}
              onClick={handleAssign}
            >
              {t('usersAdmin.clubDialog.submit')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          {dialogError && <Alert tone="danger">{dialogError}</Alert>}
          <FormField label={t('usersAdmin.clubDialog.club')} required>
            <Select
              placeholder={t('usersAdmin.clubDialog.clubPlaceholder')}
              value={assignForm.clubId}
              onChange={(event) => setAssignForm({ ...assignForm, clubId: event.target.value })}
            >
              {(clubsQuery.data ?? []).map((club) => (
                <option key={club.id} value={club.id}>
                  {club.name}
                  {club.city ? ` (${club.city})` : ''}
                </option>
              ))}
            </Select>
          </FormField>
          <FormField label={t('usersAdmin.clubDialog.role')}>
            <Select
              value={assignForm.role}
              onChange={(event) => setAssignForm({ ...assignForm, role: event.target.value })}
            >
              {CLUB_ROLES.map((role) => (
                <option key={role} value={role}>
                  {t(`clubRoles.${role}`, { defaultValue: role })}
                </option>
              ))}
            </Select>
          </FormField>
        </div>
      </Modal>

      {/* Nadanie nowego hasla */}
      <Modal
        open={passwordFor !== null}
        onClose={() => setPasswordFor(null)}
        size="sm"
        title={t('usersAdmin.passwordDialog.title')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setPasswordFor(null)}>
              {t('usersAdmin.passwordDialog.cancel')}
            </Button>
            <Button
              loading={passwordMutation.isPending}
              disabled={newPassword.length < 8}
              onClick={handlePassword}
            >
              {t('usersAdmin.passwordDialog.submit')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          {dialogError && <Alert tone="danger">{dialogError}</Alert>}
          {passwordFor && (
            <p className="text-fg-muted text-sm">
              {t('usersAdmin.passwordDialog.body', {
                name: fullNameOf(passwordFor),
                email: passwordFor.email,
              })}
            </p>
          )}
          <FormField
            label={t('usersAdmin.passwordDialog.password')}
            hint={t('usersAdmin.passwordDialog.hint')}
            required
          >
            <PasswordInput
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
            />
          </FormField>
        </div>
      </Modal>

      {/* Potwierdzenie usuniecia */}
      <Modal
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        size="sm"
        title={t('usersAdmin.confirmDelete.title')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeleting(null)}>
              {t('usersAdmin.confirmDelete.keep')}
            </Button>
            <Button variant="danger" loading={deleteMutation.isPending} onClick={handleDelete}>
              {t('usersAdmin.confirmDelete.confirm')}
            </Button>
          </>
        }
      >
        {deleting &&
          t('usersAdmin.confirmDelete.body', {
            name: fullNameOf(deleting),
            email: deleting.email,
          })}
      </Modal>
    </div>
  );
}
